import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { fetchFacilities } from '../services/api';
import { useAuth } from './AuthContext';

const FacilityContext = createContext(null);

export function useFacility() {
  const context = useContext(FacilityContext);
  if (!context) {
    throw new Error('useFacility must be used within a FacilityProvider');
  }
  return context;
}

export function FacilityProvider({ children }) {
  const { user } = useAuth();
  const [facilities, setFacilities]             = useState([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [facilitiesError, setFacilitiesError]   = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);

  useEffect(() => {
    if (!user) {
      setFacilities([]);
      setFacilitiesError(null);
      setFacilitiesLoading(false);
      return;
    }

    setFacilitiesLoading(true);
    setFacilitiesError(null);
    fetchFacilities()
      .then((data) => setFacilities(data.facilities ?? []))
      .catch((err) => {
        const msg = err.response?.data?.error || 'Failed to load facilities';
        setFacilitiesError(msg);
        console.error('FacilityContext: failed to fetch facilities:', err);
      })
      .finally(() => setFacilitiesLoading(false));
  }, [user]);

  // Append a newly created facility returned from the POST response
  const addFacility = useCallback((facility) => {
    setFacilities((prev) => [...prev, facility].sort((a, b) => a.name.localeCompare(b.name)));
  }, []);

  const selectFacility = useCallback((facilityOrId) => {
    if (!facilityOrId) { setSelectedFacility(null); return; }
    const facility = typeof facilityOrId === 'object'
      ? facilityOrId
      : facilities.find((f) => f.id === facilityOrId);
    setSelectedFacility(facility || null);
  }, [facilities]);

  const clearFacility = useCallback(() => {
    setSelectedFacility(null);
  }, []);

  // Merge changes into an existing facility in the list and in selectedFacility if matched
  const updateFacility = useCallback((id, changes) => {
    setFacilities((prev) => prev.map((f) => (f.id === id ? { ...f, ...changes } : f)));
    setSelectedFacility((prev) => (prev?.id === id ? { ...prev, ...changes } : prev));
  }, []);

  return (
    <FacilityContext.Provider value={{
      facilities,
      facilitiesLoading,
      facilitiesError,
      selectedFacility,
      selectFacility,
      clearFacility,
      addFacility,
      updateFacility,
    }}>
      {children}
    </FacilityContext.Provider>
  );
}
